import React from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import Seo, { breadcrumbSchema, faqSchema } from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { BookButton, SectionHeading } from "@/components/ui-bits";
import { EXTENDED_AUDIENCES, IMAGES, HOTEL } from "@/lib/site";

const BENEFITS = [
  { icon: "UtensilsCrossed", title: "In-Room Kitchenettes", copy: "Microwave and refrigerator in every room, with full kitchenettes in extended-stay suites for real home cooking." },
  { icon: "CalendarDays", title: "Weekly & Monthly Rates", copy: "The longer you stay, the more you save. Call us to build a rate around your project length." },
  { icon: "WashingMachine", title: "On-Site Laundry", copy: "Keep up with laundry throughout multi-week assignments without leaving the property." },
  { icon: "Truck", title: "Truck & Equipment Parking", copy: "Easy in-and-out parking for work trucks and trailers, with trucker discounts available." },
  { icon: "Wifi", title: "Reliable Free WiFi", copy: "Fast internet for remote work, telehealth shifts and staying connected with family back home." },
  { icon: "Dog", title: "Pet Friendly", copy: "Bring your companion along — a home away from home should include the whole family." },
];

const STAY_FAQS = [
  { q: "How do weekly and monthly rates work?", a: "We offer discounted nightly rates for stays of a week or longer. Call 620-232-2800 and we'll tailor pricing to your length of stay." },
  { q: "Is there a kitchen in the room?", a: "Every room has a microwave and refrigerator; our extended-stay suites add a full kitchenette." },
  { q: "Can I park a work truck?", a: "Yes — we offer easy in-and-out truck parking with trucker discounts, ideal for crews and commercial drivers." },
];

export default function ExtendedStay() {
  return (
    <>
      <Seo
        title="Extended Stay Pittsburg KS | Weekly & Monthly Hotel Rates"
        description="Extended stay in Pittsburg, Kansas for travel nurses, contractors, corporate housing and relocating families. Weekly & monthly rates, kitchenettes, laundry and truck parking at Regency Inn & Suites."
        path="/extended-stay"
        image={IMAGES.roomWork}
        schema={[
          breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Extended Stay", path: "/extended-stay" }]),
          faqSchema(STAY_FAQS),
        ]}
      />
      <PageHero
        eyebrow="Extended Stay"
        title="Your home away from home in Pittsburg."
        subtitle="Weekly and monthly stays built for travel nurses, contractors, relocating families and every professional who needs more than a night."
        image={IMAGES.roomWork}
        crumbs={[{ name: "Home", path: "/" }, { name: "Extended Stay", path: "/extended-stay" }]}
      />

      <section className="bg-bone py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <SectionHeading eyebrow="Why Regency" title="Space to live, not just sleep." />
            <p className="mt-6 text-stone leading-relaxed">
              A long assignment shouldn't mean a cramped room and takeout every night. At Regency Inn & Suites you get a
              comfortable, affordable base with a kitchenette, a real workspace, on-site laundry and a warm team that
              remembers your name — all just off US Highway 69, minutes from Pittsburg's hospitals, campus and job sites.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <BookButton label="Check Availability" testid="extended-book" />
              <a href={HOTEL.phoneHref} data-testid="extended-call" className="inline-flex items-center rounded-full border border-forest text-forest px-8 py-4 text-sm font-medium hover:bg-forest hover:text-bone transition-colors">
                Ask About Monthly Rates
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="img-zoom aspect-[4/5] rounded-md">
              <img src={IMAGES.roomBoutique} alt="Extended stay suite at Regency Inn & Suites" className="w-full h-full object-cover" loading="lazy" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-sand py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Reveal><SectionHeading eyebrow="Built for the long haul" title="Everything a longer stay needs." /></Reveal>
          <Stagger className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12">
            {BENEFITS.map((b) => {
              const Icon = Icons[b.icon] || Icons.Check;
              return (
                <StaggerItem key={b.title}>
                  <div className="border-t border-stone/20 pt-6">
                    <Icon className="text-amber" size={28} strokeWidth={1.3} />
                    <h3 className="font-serif text-2xl text-forest mt-4">{b.title}</h3>
                    <p className="text-stone mt-2 leading-relaxed text-[15px]">{b.copy}</p>
                  </div>
                </StaggerItem>
              );
            })}
          </Stagger>
        </div>
      </section>

      <section className="bg-forest text-bone py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Reveal><SectionHeading eyebrow="Who Stays With Us" title="A trusted base for traveling professionals." light /></Reveal>
          <Stagger className="mt-14 flex flex-wrap gap-3">
            {EXTENDED_AUDIENCES.map((a) => (
              <StaggerItem key={a}>
                <span className="inline-block rounded-full border border-bone/25 px-5 py-2.5 text-sm text-bone/90 hover:border-amber hover:text-amber transition-colors">
                  {a}
                </span>
              </StaggerItem>
            ))}
          </Stagger>
          <Reveal delay={0.1}>
            <div className="mt-14">
              <BookButton label="Book Your Extended Stay" testid="extended-book-2" />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
